/**
 * Utility functions for salary conversion
 */

// Currency conversion rates (same as in lambda/algolia.ts and lambda/utils/salaryAggregation.ts)
const CURRENCY_RATES: Record<string, number> = {
  EUR: 1,
  USD: 0.8681,
  GBP: 1.1360,
  CAD: 0.6164,
  AUD: 0.5595,
  CHF: 1.0766,
  SEK: 0.0907,
  NOK: 0.0848,
  DKK: 0.1339,
  CZK: 0.0413,
  HUF: 0.0026,
  PLN: 0.2356,
  ISK: 0.0068,
  ILS: 0.2645,
  JPY: 0.0055,
  MXN: 0.0470,
  NZD: 0.4864,
  TRY: 0.0205,
  KRW: 0.0006,
  CNY: 0.1221,
  BRL: 0.1616,
  INR: 0.0097,
  IDR: 0.000052,
  HKD: 0.1115,
  SGD: 0.6638,
  MYR: 0.2092,
  PHP: 0.0147,
  THB: 0.0268,
  ZAR: 0.0500,
  RON: 0.1965,
  BGN: 0.5113,
  RUB: 0.0110,
  SAR: 0.2327,
  AED: 0.2376,
  BHD: 2.3202,
  QAR: 0.2397,
  OMR: 2.2696,
  CLP: 0.001,
  COP: 0.00023,
  CRC: 0.0017,
};

export interface ConvertedSalary {
  min: number;
  max: number;
  currency: string;
  period: 'monthly';
}

export interface OriginalSalary {
  min: number;
  max: number;
  currency: string;
  period: 'annual' | 'monthly' | 'daily' | 'hourly';
}

/**
 * Convert salary to EUR per month
 */
export function convertSalaryToMonthlyEUR(
  min: number | undefined,
  max: number | undefined,
  currency: string | undefined,
  period: 'annual' | 'monthly' | 'daily' | 'hourly' | undefined
): ConvertedSalary | null {
  if (min === undefined && max === undefined) {
    return null;
  }

  const originalCurrency = currency || 'EUR';
  const originalPeriod = period || 'monthly';
  
  // Check if currency is supported
  const rate = CURRENCY_RATES[originalCurrency];
  if (rate === undefined) {
    // Currency not supported, return null
    return null;
  }

  // Convert to monthly
  let monthlyMin: number | undefined;
  let monthlyMax: number | undefined;

  if (originalPeriod === 'annual') {
    monthlyMin = min !== undefined ? min / 12 : undefined;
    monthlyMax = max !== undefined ? max / 12 : undefined;
  } else if (originalPeriod === 'hourly') {
    monthlyMin = min !== undefined ? min * 160 : undefined; // 160 hours per month
    monthlyMax = max !== undefined ? max * 160 : undefined;
  } else if (originalPeriod === 'daily') {
    monthlyMin = min !== undefined ? min * 22 : undefined; // ~22 workdays/month
    monthlyMax = max !== undefined ? max * 22 : undefined;
  } else {
    // Already monthly or unknown (default to monthly)
    monthlyMin = min;
    monthlyMax = max;
  }

  // Convert currency to EUR
  const eurMin = monthlyMin !== undefined ? Math.round(monthlyMin * rate) : undefined;
  const eurMax = monthlyMax !== undefined ? Math.round(monthlyMax * rate) : undefined;

  if (eurMin === undefined && eurMax === undefined) {
    return null;
  }

  return {
    min: eurMin ?? eurMax ?? 0,
    max: eurMax ?? eurMin ?? 0,
    currency: 'EUR',
    period: 'monthly',
  };
}

/**
 * Check if original salary differs from EUR/month
 */
export function hasOriginalSalary(
  currency: string | undefined,
  period: 'annual' | 'monthly' | 'daily' | 'hourly' | undefined
): boolean {
  const originalCurrency = currency || 'EUR';
  const originalPeriod = period || 'monthly';
  
  return originalCurrency !== 'EUR' || originalPeriod !== 'monthly';
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: string | undefined): string {
  const currencyMap: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    JPY: '¥',
    CNY: '¥',
    KRW: '₩',
    INR: '₹',
    BRL: 'R$',
    MXN: '$',
    CAD: 'C$',
    AUD: 'A$',
    NZD: 'NZ$',
    CHF: 'CHF',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    HUF: 'Ft',
    CZK: 'Kč',
    TRY: '₺',
    ZAR: 'R',
    SGD: 'S$',
    HKD: 'HK$',
    AED: 'AED',
    SAR: 'SAR',
  };
  
  return currencyMap[currency || 'EUR'] || (currency || 'EUR');
}
